import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useApiQuery } from '../hooks/useApiQuery';
import toast from 'react-hot-toast';
import { hasRole, getRoleLoginPath, UserRole } from '../utils/roleUtils';
import {
  AUTH_REQUIRED_TOAST_ID,
  AUTH_ROLE_TOAST_ID,
  extractUser,
  getAuthQueryKey,
  getCurrentUserRequest,
  getStoredToken,
  setStoredRole,
} from '../utils/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: UserRole;
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const location = useLocation();
  const hasShownAuthToast = useRef(false);
  const hasShownRoleToast = useRef(false);

  const hasToken = !!getStoredToken();
  const { data, isLoading, error } = useApiQuery<unknown>({
    queryKey: getAuthQueryKey(requiredRole),
    request: getCurrentUserRequest(requiredRole),
    enabled: hasToken,
    retry: false,
  });
  const user = hasToken ? extractUser(data, requiredRole) : undefined;

  useEffect(() => {
    if (user?.role) {
      setStoredRole(user.role);
    }
  }, [user?.role]);


  if (!hasToken) {
    return <Navigate to={getRoleLoginPath(requiredRole)} state={{ from: location }} replace />;
  }

  if (isLoading) {
    return null
  }

  if (error || !user) {
    if (!hasShownAuthToast.current) {
      toast.error('Please login to access this page', { id: AUTH_REQUIRED_TOAST_ID });
      hasShownAuthToast.current = true;
    }
    return <Navigate to={getRoleLoginPath(requiredRole)} state={{ from: location }} replace />;
  }

  if (!hasRole(user, requiredRole)) {
    if (!hasShownRoleToast.current) {
      toast.error(`You are not authorized to access this page. This page requires ${requiredRole} role.`, {
        id: AUTH_ROLE_TOAST_ID,
      });
      hasShownRoleToast.current = true;
    }
    return <Navigate to={getRoleLoginPath(requiredRole)} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
